import { useField } from 'formik';
import { useState, useEffect, useRef } from 'react';

const FloatingLabelInput = ({
  label,
  className = '',
  onFocus,
  onBlur,
  value: controlledValue,
  onChange: controlledOnChange,
  name,
  ...props
}) => {
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;
  const isSelect = props.as === 'select';
  const isTextarea = props.as === 'textarea';

  const [field, meta] = name && !isControlled ? useField({ name, ...props }) : [{}, {}];

  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const value = isControlled ? controlledValue : field?.value ?? '';
  const onChange = isControlled ? controlledOnChange : field?.onChange;

  // Track focus
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);
    el.addEventListener('focus', handleFocus);
    el.addEventListener('blur', handleBlur);

    return () => {
      el.removeEventListener('focus', handleFocus);
      el.removeEventListener('blur', handleBlur);
    };
  }, []);

  const showLabel =
    isFocused || (isSelect || isTextarea ? !!value && value !== '' : value?.toString().length > 0);

  const sharedClass = `
    w-full border rounded-10 border-gray-400 text-md outline-none
    focus:ring-2 focus:border-primary
    bg-white
  `;

  return (
    <div className={`relative w-full ${className}`}>
      <label
        htmlFor={props.id || name}
        className={`
          absolute left-3 top-2 z-10 pointer-events-none
          transition-all duration-200 ease-out
          transform origin-left text-sm text-gray-600
          ${(showLabel || isSelect) ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `}
      >
        {label}
      </label>

      {isSelect ? (
        <select
          id={props.id || name}
          name={name}
          ref={inputRef}
          value={value}
          onChange={onChange}
          className={`${sharedClass} appearance-none px-3 pb-2 pt-8`}
          onBlur={(e) => {
            if (!isControlled && field?.onBlur) field.onBlur(e);
            if (onBlur) onBlur(e);
          }}
          {...props}
        >
          {props.children}
        </select>
      ) : isTextarea ? (
        <textarea
          id={props.id || name}
          name={name}
          ref={inputRef}
          value={value}
          onChange={onChange}
          className={`${sharedClass} placeholder-gray-500 px-3 py-5 resize-none ${showLabel ? 'pb-2 pt-8' : ''}`}
          placeholder={!showLabel ? label : ''}
          rows={props.rows || 4}
          onBlur={(e) => {
            if (!isControlled && field?.onBlur) field.onBlur(e);
            if (onBlur) onBlur(e);
          }}
          {...props}
        />
      ) : (
        <input
          id={props.id || name}
          name={name}
          type={props.type || 'text'}
          ref={inputRef}
          value={value}
          onChange={onChange}
          className={`
            ${sharedClass} px-3 py-5
            placeholder-gray-500
            ${showLabel ? 'pb-2 pt-8' : ''}
          `}
          placeholder={!showLabel ? label : ''}
          onBlur={(e) => {
            if (!isControlled && field?.onBlur) field.onBlur(e);
            if (onBlur) onBlur(e);
          }}
          {...props}
        />
      )}

      {meta?.touched && meta?.error && (
        <div className="text-red-500 text-sm mt-1">{meta.error}</div>
      )}
    </div>
  );
};

export default FloatingLabelInput;
